import React from "react";

import { css, cx } from "@emotion/css";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import { getTelemonitoringTierTranslation, getTelemonitoringTierDescription } from "@/models/CarePlanModel.ts";
import { diabetesTypeTranslationMapping, medicationSchemaTranslationMapping } from "@/models/ConditionModel.ts";
import { pumpDeviceBrands, pumpModelTranslationMapping } from "@/models/MedicalDeviceModel.ts";
import { inclusionCriteriaTranslationMapping } from "@/models/TelemonitoringCriteriaModel.ts";
import { Queries } from "@/queries/Queries.ts";
import type { Theme } from "@/theme/CreateTheme.ts";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { DiabetesTypeFormField } from "@/uiKit/molecules/PatientCreationAndRenewal/DiabetesTypeFormField.tsx";
import { GestationalFormWarning } from "@/uiKit/molecules/PatientCreationAndRenewal/GestationalFormWarning.tsx";
import { HSPFormField } from "@/uiKit/molecules/PatientCreationAndRenewal/HSPFormField.tsx";
import { InclusionCriteriaFormField } from "@/uiKit/molecules/PatientCreationAndRenewal/InclusionCriteriaFormField.tsx";
import { InsulinSchemeFormField } from "@/uiKit/molecules/PatientCreationAndRenewal/InsulinSchemeFormField.tsx";
import { NurseSelectFormField } from "@/uiKit/molecules/PatientCreationAndRenewal/NurseSelectFormField.tsx";
import { ObjectivesFormField } from "@/uiKit/molecules/PatientCreationAndRenewal/ObjectivesFormField.tsx";
import { PrescriptionDurationFormField } from "@/uiKit/molecules/PatientCreationAndRenewal/PrescriptionDurationFormField.tsx";
import { PumpBrandFormField } from "@/uiKit/molecules/PatientCreationAndRenewal/PumpBrandFormField.tsx";
import { ReferralLetterFormField } from "@/uiKit/molecules/PatientCreationAndRenewal/ReferralLetterFormField.tsx";
import { TermDateFormField } from "@/uiKit/molecules/PatientCreationAndRenewal/TermDateFormField.tsx";
import { TreatmentPrescriptionFormField } from "@/uiKit/molecules/PatientCreationAndRenewal/TreatmentPrescriptionFormField.tsx";
import { usePatientCreationContext } from "@/uiKit/organisms/PatientCreationAndRenewal/PatientCreationProvider.tsx";
import { useResetInsulinSchemeWhenInvalidDiabetesType } from "@/uiKit/organisms/PatientCreationAndRenewal/utils.ts";

export type InitialCarePlanCreationFormContentProps = {
  patientInfo: {
    givenName: string;
    familyName: string;
  };
  className?: string;
};

export const InitialCarePlanCreationFormContent: React.FC<InitialCarePlanCreationFormContentProps> = ({
  patientInfo,
  className,
}) => {
  const {
    allowedDiabetesTypes,
    allowedInsulinScheme,
    telemonitoringCriteriaMap,
    telemonitoringTierMap,
    telemonitoringNurses,
  } = usePatientCreationContext();
  const formContext = useFormContext();
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const providers = Queries.practitioner.useHSPList({});
  const diabetesType = useWatch({
    name: "diabetesType",
    control: formContext.control,
  });
  const insulinScheme = useWatch({
    name: "insulinScheme",
    control: formContext.control,
  });

  useResetInsulinSchemeWhenInvalidDiabetesType(formContext, diabetesType, insulinScheme);

  return (
    <CardContent className={cx(styles.root, className)} formExtraPadding>
      <Typography variant="subtitle" className={styles.title}>
        {t("diabetesForms.diabetesAndSchema")}
      </Typography>
      <DiabetesTypeFormField
        allowedDiabetesTypes={allowedDiabetesTypes}
        translationMapping={type => diabetesTypeTranslationMapping(type, undefined)}
      />
      <InsulinSchemeFormField
        diabetesType={diabetesType}
        allowedInsulinSchemes={allowedInsulinScheme}
        translationMapping={medicationSchemaTranslationMapping}
      />
      {insulinScheme === "insulinPump" && (
        <PumpBrandFormField brands={pumpDeviceBrands} translationMapping={pumpModelTranslationMapping} />
      )}
      {diabetesType === "gestational" && <TermDateFormField />}
      {insulinScheme !== "noInsulin" && (
        <>
          <Typography variant="subtitle" className={styles.title}>
            {t("diabetesForms.telemonitoring")}
          </Typography>
          <InclusionCriteriaFormField
            diabetesType={diabetesType}
            insulinScheme={insulinScheme}
            criteriaMap={telemonitoringCriteriaMap}
            tierMap={telemonitoringTierMap}
            criteriaTranslationMapping={inclusionCriteriaTranslationMapping}
            tierTranslationMapping={getTelemonitoringTierTranslation}
            tierDescriptionTranslationMapping={getTelemonitoringTierDescription}
          />
          <PrescriptionDurationFormField translationMapping={value => `${value} ${t("diabetesForms.months")}`} />
          {telemonitoringNurses.length > 0 && <NurseSelectFormField nurses={telemonitoringNurses} />}
          {insulinScheme === "insulinPump" && <HSPFormField providers={providers} />}
          <TreatmentPrescriptionFormField patientInfo={patientInfo} />
          <ReferralLetterFormField patientInfo={patientInfo} />
          <ObjectivesFormField patientInfo={patientInfo} />
        </>
      )}
      {insulinScheme === "noInsulin" && <GestationalFormWarning />}
    </CardContent>
  );
};

const makeStyles = (theme: Theme) => ({
  root: css({
    display: "flex",
    flexDirection: "column",
  }),
  title: css({
    paddingBottom: theme.spacing(12),
  }),
});
